import React from 'react';
import { Shield, CheckCircle, Award, Zap, Wrench, Eye } from 'lucide-react';

export default function AboutOutrans() {
  return (
    <main className="min-h-screen bg-gradient-to-br from-[#0a0f0a] via-[#0d1a12] to-[#070d08] text-white pt-20 px-4 pb-16">
      <section className="max-w-6xl mx-auto">
        <p className="text-[#d4af37] font-semibold uppercase text-sm tracking-[0.3em] mb-4 font-[Cinzel]">
          About OUTRAN
        </p>
        <h1 className="text-6xl md:text-8xl font-light tracking-wider mb-8 text-[#d4af37] font-[Cormorant_Garamond]">
          OUTRAN
        </h1>
        
        <div className="mb-16 max-w-4xl">
          <p className="text-zinc-200 text-2xl md:text-3xl leading-relaxed font-light tracking-wide font-[Cormorant_Garamond]">
            OUTRAN is a motorcycle-accessory brand built around a clear design philosophy: every product must earn its place on the motorcycle. Each accessory is developed with close attention to fit, function, durability and visual integration, so it performs reliably while complementing the original character of the bike.
          </p>
        </div>

        {/* Design Philosophy */}
        <div className="mb-20">
          <h2 className="text-4xl md:text-5xl font-light text-white mb-10 tracking-wider border-b border-[#d4af37]/30 pb-4 font-[Cormorant_Garamond]">
            Design Philosophy
          </h2>
          <div className="bg-gradient-to-br from-[#d4af37]/5 to-transparent border border-[#d4af37]/20 rounded-2xl p-10 backdrop-blur-sm">
            <p className="text-zinc-200 leading-relaxed text-xl md:text-2xl font-light tracking-wide font-[Cormorant_Garamond]">
              Products are meticulously designed, prototyped, fitted and tested before moving into production. Materials, stitching, hardware, mounting points and finishing are reviewed through defined quality-control checks, and any product that does not meet OUTRAN's standards is reworked or rejected. Nothing is launched simply because it looks ready. It is released only when the fit, finish and real-world usability are considered ready for the rider.
            </p>
          </div>
        </div>

        {/* Quality Standards */}
        <div className="mb-20">
          <h2 className="text-4xl md:text-5xl font-light text-white mb-10 tracking-wider border-b border-[#d4af37]/30 pb-4 font-[Cormorant_Garamond]">
            Quality Standards
          </h2>
          <div className="grid md:grid-cols-2 gap-8">
            {[
              { icon: Wrench, title: 'Meticulous Design', desc: 'Every product is designed, prototyped, fitted and tested before production' },
              { icon: Shield, title: 'Quality Control', desc: 'Materials, stitching, hardware, mounting points and finishing are thoroughly reviewed' },
              { icon: CheckCircle, title: 'Standards First', desc: 'Products that don\'t meet standards are reworked or rejected' },
              { icon: Eye, title: 'Real-World Ready', desc: 'Released only when fit, finish and usability are ready for the rider' }
            ].map((feature, index) => (
              <div key={index} className="bg-gradient-to-br from-black/40 to-black/20 border border-[#d4af37]/20 rounded-2xl p-8 hover:border-[#d4af37]/50 transition-all duration-300 hover:shadow-lg hover:shadow-[#d4af37]/10">
                <feature.icon className="text-[#d4af37] mb-6" size={40} />
                <h3 className="text-2xl font-semibold uppercase mb-3 tracking-wider font-[Cinzel]">{feature.title}</h3>
                <p className="text-zinc-300 text-lg leading-relaxed font-light font-[Cormorant_Garamond]">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Product Delivery */}
        <div className="mb-20">
          <h2 className="text-4xl md:text-5xl font-light text-white mb-10 tracking-wider border-b border-[#d4af37]/30 pb-4 font-[Cormorant_Garamond]">
            Product Delivery
          </h2>
          <div className="bg-gradient-to-br from-[#d4af37]/5 to-transparent border border-[#d4af37]/20 rounded-2xl p-10 backdrop-blur-sm">
            <p className="text-zinc-200 leading-relaxed text-xl md:text-2xl font-light tracking-wide font-[Cormorant_Garamond]">
              OUTRAN is equally particular about product delivery. Every unit is inspected before dispatch to ensure the correct model compatibility, construction quality and overall presentation. This disciplined approach allows OUTRAN to create fewer, better-developed products backed by dependable support and a more confident ownership experience.
            </p>
          </div>
        </div>

        {/* Partnership */}
        <div className="mb-20">
          <h2 className="text-4xl md:text-5xl font-light text-white mb-10 tracking-wider border-b border-[#d4af37]/30 pb-4 font-[Cormorant_Garamond]">
            Partnership with RRide Moto Studio
          </h2>
          <div className="bg-gradient-to-br from-[#d4af37]/10 to-black/40 border border-[#d4af37]/30 rounded-2xl p-10 backdrop-blur-sm">
            <div className="flex items-center gap-4 mb-6">
              <Award className="text-[#d4af37]" size={48} />
              <h3 className="text-3xl font-semibold uppercase tracking-wider font-[Cinzel]">Authorised Retail Partner</h3>
            </div>
            <p className="text-zinc-200 leading-relaxed text-xl md:text-2xl font-light tracking-wide font-[Cormorant_Garamond]">
              RRide Moto Studio is the first authorised retail partner for OUTRAN Tank Covers, offering riders genuine Outran products, compatibility guidance, installation assistance and after-sales support.
            </p>
          </div>
        </div>

        {/* Key Features */}
        <div>
          <h2 className="text-4xl md:text-5xl font-light text-white mb-10 tracking-wider border-b border-[#d4af37]/30 pb-4 font-[Cormorant_Garamond]">
            What OUTRAN Stands For
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { icon: Zap, title: 'Function First', desc: 'Every product must earn its place on the motorcycle' },
              { icon: Shield, title: 'Durability', desc: 'Built to last with quality materials and construction' },
              { icon: CheckCircle, title: 'Visual Integration', desc: 'Complements the original character of the bike' },
              { icon: Wrench, title: 'Rigorous Testing', desc: 'Thoroughly tested before production begins' },
              { icon: Eye, title: 'Quality Control', desc: 'Defined checks for materials, stitching and finishing' },
              { icon: Award, title: 'Dependable Support', desc: 'Backed by reliable after-sales service' }
            ].map((feature, index) => (
              <div key={index} className="bg-gradient-to-br from-black/40 to-black/20 border border-[#d4af37]/20 rounded-2xl p-8 hover:border-[#d4af37]/50 transition-all duration-300 hover:shadow-lg hover:shadow-[#d4af37]/10">
                <feature.icon className="text-[#d4af37] mb-6" size={40} />
                <h3 className="text-2xl font-semibold uppercase mb-3 tracking-wider font-[Cinzel]">{feature.title}</h3>
                <p className="text-zinc-300 text-lg leading-relaxed font-light font-[Cormorant_Garamond]">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}
